import React, { useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faStore, faPlus, faShop, faUserFriends, faCog, faShieldHalved } from '@fortawesome/free-solid-svg-icons';
import useAuthStore from '../../store/authStore';
import { getTokenInfo } from '../../helpers/tokenHelper';
import UsersList from '../../components/admin/users/UsersList';
import UserForm from '../../components/admin/users/UserForm';
import UserDetailModal from '../../components/admin/users/UserDetailModal';
import MarcasList from '../../components/admin/marcas/MarcasList';
import TiendasList from '../../components/admin/tiendas/TiendasList';
import ClientesList from '../../components/admin/clientes/ClientesList';
import SystemManagement from '../../components/admin/system/SystemManagement';
import SecurityManagement from '../../components/admin/security/SecurityManagement';
import { del } from '../../helpers/apiHelper';

const AdminPanel = () => {
  const { user } = useAuthStore();
  const [activeSection, setActiveSection] = useState('users'); // 'users', 'marcas', 'tiendas', 'clientes', 'system' o 'security'
  const [showUserForm, setShowUserForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetail, setShowUserDetail] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const tokenInfo = getTokenInfo();
  const userRole = tokenInfo?.role || 'user';

  const handleUserFormSuccess = useCallback((userData) => {
    setShowUserForm(false);
    setSelectedUser(null);
    setRefreshKey(prev => prev + 1);
  }, []);

  const handleEditUser = useCallback((user) => {
    setSelectedUser(user);
    setShowUserForm(true);
  }, []);

  const handleViewUserDetail = useCallback((user) => {
    setSelectedUser(user);
    setShowUserDetail(true);
  }, []);

  const handleDeleteUser = useCallback(async (userId) => {
    if (!userId) {
      console.error('No user ID provided for deletion');
      return;
    }

    if (window.confirm('¿Estás seguro de que deseas eliminar este usuario?')) {
      try {
        await del(`users/${userId}`);
        setRefreshKey(prev => prev + 1);
        alert('Usuario eliminado con éxito');
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Error al eliminar el usuario: ' + error.message);
      }
    }
  }, []);

  const handleCreateUser = useCallback(() => {
    setSelectedUser(null);
    setShowUserForm(true);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center py-4 sm:py-6 space-y-4 sm:space-y-0">
            <div className="w-full sm:w-auto">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 break-words">Panel de Administración</h1>
              <p className="mt-1 text-sm text-gray-500 break-words">
                Bienvenido, {user?.email || 'Administrador'} - Rol: {userRole}
              </p>
            </div>
          </div>

          {/* Navegación */}
          <div className="flex space-x-4 mt-4 border-b overflow-x-auto">
            <button
              onClick={() => setActiveSection('users')}
              className={`py-2 px-4 font-medium text-sm whitespace-nowrap ${activeSection === 'users'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              <FontAwesomeIcon icon={faUsers} className="mr-2" />
              Usuarios
            </button>
            <button
              onClick={() => setActiveSection('marcas')}
              className={`py-2 px-4 font-medium text-sm whitespace-nowrap ${activeSection === 'marcas'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              <FontAwesomeIcon icon={faStore} className="mr-2" />
              Marcas
            </button>
            <button
              onClick={() => setActiveSection('tiendas')}
              className={`py-2 px-4 font-medium text-sm whitespace-nowrap ${activeSection === 'tiendas'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              <FontAwesomeIcon icon={faShop} className="mr-2" />
              Tiendas
            </button>
            <button
              onClick={() => setActiveSection('clientes')}
              className={`py-2 px-4 font-medium text-sm whitespace-nowrap ${activeSection === 'clientes'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              <FontAwesomeIcon icon={faUserFriends} className="mr-2" />
              Clientes
            </button>
            <button
              onClick={() => setActiveSection('system')}
              className={`py-2 px-4 font-medium text-sm whitespace-nowrap ${activeSection === 'system'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              <FontAwesomeIcon icon={faCog} className="mr-2" />
              Sistema
            </button>
            <button
              onClick={() => setActiveSection('security')}
              className={`py-2 px-4 font-medium text-sm whitespace-nowrap ${activeSection === 'security'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              <FontAwesomeIcon icon={faShieldHalved} className="mr-2" />
              Seguridad
            </button>
          </div>
        </div>
      </div>

      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Botón de acción según la sección activa */}
        <div className="mb-6 flex justify-end">
          {activeSection === 'users' && (
            <button
              onClick={handleCreateUser}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>Nuevo Usuario</span>
            </button>
          )}
        </div>

        {/* Contenido según la sección activa */}
        {activeSection === 'users' ? (
          <>
            <UsersList
              key={refreshKey}
              onEdit={handleEditUser}
              onView={handleViewUserDetail}
              onDelete={handleDeleteUser}
            />

            {/* Modales de usuario */}
            {showUserForm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50 overflow-y-auto">
                <div className="w-full max-w-lg mx-auto">
                  <UserForm
                    user={selectedUser}
                    onClose={() => {
                      setShowUserForm(false);
                      setSelectedUser(null);
                    }}
                    onSuccess={handleUserFormSuccess}
                  />
                </div>
              </div>
            )}

            {showUserDetail && selectedUser && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50 overflow-y-auto">
                <div className="w-full max-w-lg mx-auto">
                  <UserDetailModal
                    user={selectedUser}
                    onClose={() => {
                      setShowUserDetail(false);
                      setSelectedUser(null);
                    }}
                  />
                </div>
              </div>
            )}
          </>
        ) : activeSection === 'marcas' ? (
          <MarcasList />
        ) : activeSection === 'tiendas' ? (
          <TiendasList />
        ) : activeSection === 'clientes' ? (
          <ClientesList />
        ) : activeSection === 'system' ? (
          <SystemManagement />
        ) : activeSection === 'security' ? (
          <SecurityManagement />
        ) : null}
      </div>
    </div>
  );
};

export default AdminPanel;
